<!--
  AB#22360 — the pattern a product on this order line is made to, fixed when
  the order was confirmed; whether that is still the product's; and, on a
  reorder of a product that moved since, the one question: the pattern from
  the order (pre-selected) or the latest. After "adjust pattern" the adjusted
  pattern is used and says why; only the PD lead goes back, with a reason.
  AB#22637 AC12 — in the adjust / do not adjust pattern words throughout.
-->
<template>
  <div v-if="line" class="opl" :data-test="`order-pattern-${line.product_id}`">
    <p class="opl-made">
      <template v-if="line.style_code">
        Made to pattern <b class="opl-mono">{{ line.style_code }}</b>
      </template>
      <template v-else-if="line.factory">
        Made to the factory pattern of <b>{{ line.factory.name }}</b>
      </template>
      <template v-else>No pattern recorded for this product</template>
      <small v-if="line.differs_from_product && line.product_now" data-test="order-pattern-differs">
        · the product is now on {{ line.product_now }}</small
      >
    </p>

    <!-- AC13/AC14 — adjust pattern: the adjusted pattern, with the line written on it. -->
    <p v-if="line.source === 'correction'" class="opl-note" data-test="order-pattern-correction">
      {{ line.note }} This reorder is made to the adjusted pattern.
    </p>
    <p
      v-else-if="line.source === 'unavailable'"
      class="opl-note"
      data-test="order-pattern-unavailable"
    >
      {{ line.note }} This reorder is made to the latest pattern.
    </p>
    <p
      v-else-if="line.source === 'lead_override'"
      class="opl-note"
      data-test="order-pattern-override"
    >
      Made to the pattern before it was adjusted, by {{ line.chosen?.by }}: {{ line.reason }}
    </p>

    <!-- AC4–AC6 — the question, only where the two patterns differ. -->
    <form
      v-if="line.question === 'open' && line.latest && line.can.choose"
      class="opl-ask"
      data-test="order-pattern-question"
      @submit.prevent="answer"
    >
      <b>This product has moved to a newer pattern since the order it repeats.</b>
      <label>
        <input v-model="choice" type="radio" value="order" data-test="order-pattern-keep" />
        The pattern from the order: <span class="opl-mono">{{ line.repeats?.style_code }}</span>
      </label>
      <label>
        <input v-model="choice" type="radio" value="latest" data-test="order-pattern-latest" />
        The latest pattern for this product:
        <span class="opl-mono">{{ line.latest.style_code }}</span>
        <small
          >{{ line.latest.active_from ? `active from ${dayLabel(line.latest.active_from)}` : ''
          }}{{ broughtBy(line.latest.brought_by) }}</small
        >
      </label>
      <button type="submit" class="dash-btn" :disabled="busy" data-test="order-pattern-confirm">
        Confirm pattern
      </button>
    </form>
    <p
      v-else-if="line.chosen && line.source !== 'lead_override'"
      class="opl-note"
      data-test="order-pattern-chosen"
    >
      {{ line.choice === 'latest' ? 'The latest pattern' : 'The pattern from the order' }} chosen by
      {{ line.chosen.by }} on {{ dayLabel(line.chosen.at) }}.
    </p>

    <!-- AC15 — going back to the faulty pattern: the PD lead only, with a reason. -->
    <form
      v-if="line.can.override && line.source === 'correction'"
      class="opl-ask"
      data-test="order-pattern-lead"
      @submit.prevent="override"
    >
      <input
        v-model="reason"
        class="dd-sel"
        maxlength="500"
        :placeholder="`Why this reorder goes back to ${line.repeats?.style_code}`"
        aria-label="Why it goes back to the pattern before it was adjusted"
        data-test="order-pattern-reason"
      />
      <button
        type="submit"
        class="dash-btn"
        :disabled="busy || !reason.trim()"
        data-test="order-pattern-goback"
      >
        Use {{ line.repeats?.style_code }} anyway
      </button>
    </form>
    <p v-if="message" class="opl-msg" role="status" data-test="order-pattern-msg">{{ message }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { KIND_LABELS } from '@/composables/useLivePatterns';
import { dayLabel } from '@/composables/useProductDevelopment';
import { useOrderPatterns, type OrderPattern } from '@/composables/useOrderPatterns';

const props = defineProps<{
  orderId: number;
  orderItemId: number | null | undefined;
  productId: number | null | undefined;
}>();

const { items, busy, load, choose } = useOrderPatterns();
const line = computed<OrderPattern | null>(
  () =>
    items.value.find(
      (p) =>
        p.product_id === props.productId &&
        (props.orderItemId === null ||
          props.orderItemId === undefined ||
          p.order_item_id === props.orderItemId)
    ) ?? null
);
const choice = ref<'order' | 'latest'>('order');
const reason = ref('');
const message = ref('');

const broughtBy = (b: NonNullable<OrderPattern['latest']>['brought_by']): string => {
  if (!b) return '';
  if ('replacement' in b)
    return `, replacing ${b.from} (${b.kind && b.kind in KIND_LABELS ? KIND_LABELS[b.kind as keyof typeof KIND_LABELS] : 'replacement'})`;

  return `, from development DEV-${b.development_id}`;
};

const answer = async (): Promise<void> => {
  if (!line.value) return;
  message.value = (await choose(props.orderId, line.value.id, choice.value)).message;
};
const override = async (): Promise<void> => {
  if (!line.value || !reason.value.trim()) return;
  const r = await choose(props.orderId, line.value.id, 'order', reason.value.trim());
  message.value = r.message;
  if (r.ok) reason.value = '';
};

onMounted(() => void load(props.orderId));
watch(
  () => props.orderId,
  (id) => void load(id)
);
</script>

<style scoped>
.opl {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 8px 12px;
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 13px;
}
.opl-made {
  margin: 0;
}
.opl-made small,
.opl-ask small {
  color: var(--ink-mute);
}
.opl-mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
.opl-note {
  margin: 0;
  color: var(--ink-soft);
}
.opl-ask {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.opl-ask label {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
}
.opl-msg {
  margin: 0;
}
</style>
